import React, { FormEvent, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Close, Visibility, VisibilityOff } from '@mui/icons-material';
import { KeyIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { t } from '../../utils/translations';

const MIN_PASSWORD_LENGTH = 8;

const emptyForm = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
};

const emptyVisibility = {
  currentPassword: false,
  newPassword: false,
  confirmPassword: false,
};

const ChangePasswordCard: React.FC = () => {
  const { changePassword } = useAuth();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [visible, setVisible] = useState(emptyVisibility);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleClose = () => {
    if (saving) return;
    setOpen(false);
    setForm(emptyForm);
    setVisible(emptyVisibility);
    setError('');
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (form.newPassword.length < MIN_PASSWORD_LENGTH) {
      setError(t.profile.passwordMinLength);
      return;
    }
    if (form.newPassword !== form.confirmPassword) {
      setError(t.profile.passwordMismatch);
      return;
    }
    if (form.currentPassword === form.newPassword) {
      setError(t.profile.passwordSameAsCurrent);
      return;
    }

    setSaving(true);
    try {
      await changePassword(form.currentPassword, form.newPassword);
      setForm(emptyForm);
      setOpen(false);
      setSuccess(t.profile.passwordUpdateSuccess);
    } catch (err) {
      setError(err instanceof Error ? err.message : t.profile.passwordUpdateError);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="mb-6 rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-2">
          <KeyIcon className="mt-0.5 h-5 w-5 shrink-0 text-brand-gold" />
          <div>
            <h2 className="text-lg font-semibold text-slate-900">{t.profile.passwordTitle}</h2>
            <p className="text-sm text-slate-500">{t.profile.passwordSubtitle}</p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => {
            setSuccess('');
            setError('');
            setOpen(true);
          }}
          className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-brand-200 bg-white px-3 py-2 text-sm font-medium text-brand-600 transition hover:bg-brand-50"
        >
          {t.profile.passwordSubmit}
        </button>
      </div>

      {success ? (
        <div className="mt-4">
          <Alert severity="success">{success}</Alert>
        </div>
      ) : null}

      <Dialog
        open={open}
        onClose={(_, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          handleClose();
        }}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
        fullScreen={fullScreen}
        PaperProps={{
          sx: {
            borderRadius: fullScreen ? 0 : 3,
            border: fullScreen ? 'none' : '1px solid',
            borderColor: 'divider',
          },
        }}
      >
        <DialogTitle
          sx={{
            position: 'relative',
            px: { xs: 2, sm: 2.5 },
            py: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            pr: 6,
          }}
        >
          <Typography component="span" sx={{ display: 'block', fontSize: '1rem', fontWeight: 600 }}>
            {t.profile.passwordTitle}
          </Typography>
          <Typography
            component="span"
            sx={{ mt: 0.5, display: 'block', fontSize: '0.875rem', color: 'text.secondary' }}
          >
            {t.profile.passwordSubtitle}
          </Typography>
          <IconButton
            aria-label={t.common.close}
            onClick={handleClose}
            disabled={saving}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent sx={{ px: { xs: 2, sm: 2.5 }, py: 2.5 }}>
            <div className="flex flex-col gap-3">
              {error ? <Alert severity="error">{error}</Alert> : null}
              {(
                [
                  {
                    key: 'currentPassword' as const,
                    label: t.profile.passwordCurrent,
                    autoComplete: 'current-password',
                    helperText: undefined,
                  },
                  {
                    key: 'newPassword' as const,
                    label: t.profile.passwordNew,
                    autoComplete: 'new-password',
                    helperText: t.profile.passwordMinLength,
                  },
                  {
                    key: 'confirmPassword' as const,
                    label: t.profile.passwordConfirm,
                    autoComplete: 'new-password',
                    helperText: undefined,
                  },
                ] as const
              ).map((field) => (
                <TextField
                  key={field.key}
                  type={visible[field.key] ? 'text' : 'password'}
                  label={field.label}
                  value={form[field.key]}
                  onChange={(e) => setForm((prev) => ({ ...prev, [field.key]: e.target.value }))}
                  autoComplete={field.autoComplete}
                  required
                  fullWidth
                  size="small"
                  disabled={saving}
                  helperText={field.helperText}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label={
                            visible[field.key] ? t.login.hidePassword : t.login.showPassword
                          }
                          onClick={() =>
                            setVisible((prev) => ({ ...prev, [field.key]: !prev[field.key] }))
                          }
                          onMouseDown={(event) => event.preventDefault()}
                          edge="end"
                          disabled={saving}
                        >
                          {visible[field.key] ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              ))}
            </div>
          </DialogContent>
          <DialogActions
            sx={{
              px: { xs: 2, sm: 2.5 },
              py: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Button onClick={handleClose} disabled={saving} variant="outlined" color="inherit">
              {t.common.cancel}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disableElevation
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {t.profile.passwordSubmit}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </section>
  );
};

export default ChangePasswordCard;
